/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { useAuth } from 'Contexts/Auth';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Api, { URLS, Machine } from 'Api/Api';
import styles from './Machines.module.css';
import { MachinesIcon } from '../components/UmatiIcons';
import { BsCheckCircle, BsXCircle, BsChevronDown, BsFillTrash3Fill, BsUpload, BsSearch, BsX } from 'react-icons/bs';
import Fuse from 'fuse.js';

type ChooseShortIdProps = {
  Id: number | undefined;
  currentShortId: string | null | undefined;
  allShortIds: (string | null | undefined)[];
  onShortIdChange: (newShortId: string, message: string, messageType: 'success' | 'danger') => void;
};

const ChooseShortId = ({ Id, currentShortId, allShortIds, onShortIdChange }: ChooseShortIdProps) => {
  const [selectedShortId, setSelectedShortId] = useState<number | undefined>(() => {
    if (currentShortId?.startsWith('M')) {
      const num = Number(currentShortId.substring(1));
      return isNaN(num) ? undefined : num;
    }
    return undefined; /* If currentShortId is null or doesn't start with "M", initialize with undefined */
  });
  const [isAvailable, setIsAvailable] = useState<boolean>(false);

  /* Check if the shortId selected in the input field is available */
  useEffect(() => {
    if (selectedShortId === undefined) {
      setIsAvailable(false);
    } else {
      setIsAvailable(!allShortIds.includes(`M${selectedShortId}`));
    }
  }, [selectedShortId, allShortIds]);

  /* Superlinter example: irregular spacing error */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedShortId === undefined)
      return; /* Exit early if -lno shortId is currently selected (e.g. on initial render or reset) */
    const selectedShortIdWPrefix = `M${selectedShortId}`;
    if (Id !== undefined) {
      try {
        await Api.Machines.editShortId(Id, selectedShortIdWPrefix);
        onShortIdChange(selectedShortIdWPrefix, 'ShortId was successfully updated.', 'success');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to update shortId: ', error);
        }
        onShortIdChange(selectedShortIdWPrefix, 'Failed to update ShortId.', 'danger');
      }
    } else {
      return;
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles['short-id-form']}>
        <span>M</span>
        <input
          type="number"
          min={0}
          max={999999}
          value={selectedShortId ?? ''}
          onChange={(e) => {
            const num = Number(e.target.value);
            setSelectedShortId(!isNaN(num) ? num : undefined);
          }}
        />
        <Button
          type="submit"
          variant={isAvailable ? 'primary' : 'outline-secondary'}
          className={`${styles['button']} ${isAvailable ? '' : styles['no-hover']}`}
          disabled={!isAvailable} // isAvailable is initialized with false -> Submit-Button is inactive per default
        >
          Submit
        </Button>
      </form>

      {!isAvailable && `M${selectedShortId}` !== currentShortId && (
        <div className={styles['validation-message']}>ID already taken. Please choose a different one.</div>
      )}
    </>
  );
};

type MachineCardProps = {
  machine: Machine;
  allShortIds: (string | null | undefined)[];
  onShortIdUpdate: (id: number, newShortId: string) => void;
  onDelete: (id: number) => void;
  onImageUpdate: (id: number, newPath: string) => void;
  expandAll: boolean;
};

const MachineCard = ({
  machine,
  allShortIds,
  onShortIdUpdate,
  onDelete,
  onImageUpdate,
  expandAll,
}: MachineCardProps) => {
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'danger' | ''>('');
  const [expanded, setExpanded] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const deleteBtnRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imgBust, setImgBust] = useState<number>(0);

  useEffect(() => {
    setExpanded(expandAll);
  }, [expandAll]);

  const openConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); /* Prevent propagation to parent to prevent toggl of card body */
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
    deleteBtnRef.current?.focus(); /* deleteBtnRef.current: pointer on button and button itself; If the delete action is cancelled, return keyboard focus to the delete button for better accessibility */
  };

  const confirmDelete = () => {
    setShowConfirm(false);
    if (machine.Id !== undefined) {
      onDelete(
        machine.Id,
      ); /* Trigger deletion of the machine currently rendered by this MachineCard instance (originating from machines.map()) */
    }
  };

  return (
    <div className={styles.card}>
      <div
        role="button"
        className={styles['card-header']}
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        <div>
          <div className={styles['card-title']}>{machine.ShortId}</div>
          <div className={styles['card-subtitle']}>
            clientId={machine.ClientId}
            <br />
            nsu={machine.ParentId}
          </div>
        </div>
        <div className={styles['card-actions']}>
          <BsChevronDown className={`${styles.arrow} ${expanded ? styles['arrow-rotated'] : ''}`} />
          <Button
            type="button"
            className={styles['delete-button']}
            title={'delete'}
            aria-label={`Delete machine ${machine.ShortId}`}
            tabIndex={0}
            onClick={openConfirm}
            onMouseDown={(e) =>
              e.stopPropagation()
            } /* Additional safety to "stopPropagation" in onClick-function of this component (to prevent toggl of the card body) */
            ref={deleteBtnRef}
          >
            <BsFillTrash3Fill aria-hidden="true" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className={styles['card-body']}>
          <div className={styles['row']}>
            <div>
              <label className={styles['label']}>Image:</label>
              <Button
                variant="link"
                size="sm"
                className={styles['upload-button']}
                title="upload"
                aria-label="Upload new image"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <BsUpload />
              </Button>
            </div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={async (e) => {
                if (!e.target.files?.length || !machine.Id) return;
                const inputEl = e.currentTarget as HTMLInputElement;
                const file = e.target.files[0];

                try {
                  const resp = await Api.Machines.uploadImg(
                    machine.Id,
                    file,
                  ); /* Store server response (http response object) in variable "resp" */
                  const basePath = resp?.data?.ImagePath ?? machine.ImagePath ?? '';
                  const nextPath = basePath.split('?')[0];
                  onImageUpdate(machine.Id, nextPath); /* Update parent state of image path */
                  setImgBust(Date.now());
                } catch (error) {
                  if (import.meta.env.DEV) {
                    console.error('Image upload failed: ', error);
                  }
                } finally {
                  if (inputEl)
                    inputEl.value =
                      ''; /* Clear the file input's value in the DOM so the same file can be selected again */
                }
              }}
            />
            <div className={styles['image-container']}>
              {machine.ImagePath ? (
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  src={
                    URLS.Machine.getImage((machine.ImagePath ?? '').split('?')[0]) + (imgBust ? `?bust=${imgBust}` : '')
                  }
                  alt={`Image of machine ${machine.ShortId ?? machine.ClientId ?? 'unknown'}`}
                />
              ) : (
                <span
                  className={styles['no-image-text']}
                  role="img"
                  aria-label={`No image found for machine ${machine.ShortId ?? machine.ClientId ?? 'unknown'}`}
                >
                  No image found for machine {machine.ShortId ?? machine.ClientId ?? 'unknown'}
                </span>
              )}
            </div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Image Path:</label>
            <div>{machine.ImagePath}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Client Id:</label>
            <div>{machine.ClientId}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Mqtt:</label>
            <div>{machine.MqttTopic}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Short Id:</label>
            <div>{machine.ShortId}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Parent Id:</label>
            <div>{machine.ParentId}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Type Def:</label>
            <div>{machine.TypeDefinition}</div>
          </div>
          <div className={styles['row']}>
            <label className={styles['label']}>Set Short Id:</label>
            <ChooseShortId
              Id={machine.Id}
              currentShortId={machine.ShortId}
              allShortIds={allShortIds} // Array of all existing ShortIds, extracted via machine.map() inside sortedMachines
              onShortIdChange={(newShortId, message, feedbackType) => {
                if (machine.Id !== undefined && feedbackType === 'success') {
                  onShortIdUpdate(machine.Id, newShortId);
                }
                setFeedbackMessage(message);
                setFeedbackType(feedbackType);
                setTimeout(() => {
                  setFeedbackMessage('');
                  setFeedbackType('');
                }, 2000);
              }}
            />
          </div>

          {feedbackMessage && (
            <div
              className={`d-flex align-items-center p-1 mt-2 mb-0 ${styles.alert} 
                ${feedbackType === 'success' ? styles['alert-success'] : styles['alert-danger']}`}
            >
              {feedbackType === 'success' ? (
                <BsCheckCircle className="me-2" size={16} />
              ) : (
                <BsXCircle className="me-2" size={16} />
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{feedbackMessage}</span>
            </div>
          )}
        </div>
      )}

      <Modal show={showConfirm} onHide={closeConfirm} centered>
        <Modal.Body>Do you really want to delete machine {machine.ShortId}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConfirm}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const MachinePage = () => {
  const auth = useAuth();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredMachines, setfilteredMachines] = useState<Machine[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!auth.user) return;

    Api.Machines.getMachines()
      .then((response) => {
        const sorted = response.data
          .slice()
          /* The actual sorting algorithm (usually Timsort) is handled by the JS engine.
            The following function only defines how two elements are compared. */
          .sort((a, b) => {
            /* Create a numeric value from the ShortId
              - Remove the "M" prefix from e.g. "M203" using substring(1)
              - Fallback to a large number for null or undefined ShortIds to ensure those machines are sorted to the end.
              - 10: parse as base-10 decimal number */
            const numA = a.ShortId?.startsWith('M') ? parseInt(a.ShortId.substring(1), 10) : 1_000_000_000;
            const numB = b.ShortId?.startsWith('M') ? parseInt(b.ShortId.substring(1), 10) : 1_000_000_000;
            return numA - numB;
          });
        setMachines(
          auth.user?.Roles?.some((role) => ['Admin', 'SuperAdmin'].includes(role))
            ? sorted
            : sorted.filter((m) => m.ClientId === auth.user?.ClientName),
        );
      })
      .catch((e) => {
        if (import.meta.env.DEV) {
          console.error(String(e));
        }
      });
  }, [auth.user]);

  const filterMachines = (machines: Machine[], searchQuery: string): Machine[] => {
    const fuse = new Fuse(machines, {
      keys: ['ShortId', 'ClientId', 'MachineId', 'TypeDefinition'],
      threshold: 0.4,
    });
    if (!searchQuery) return machines;
    return fuse.search(searchQuery).map((result) => result.item);
  };

  useEffect(() => {
    const results = filterMachines(machines, searchQuery);
    setfilteredMachines(results);
  }, [machines, searchQuery]);

  const allShortIds = useMemo(() => machines.map((m) => m.ShortId), [machines]);

  const handleShortIdUpdate = (id: number, newShortId: string) => {
    setMachines((prevMachines) =>
      prevMachines.map((m) => {
        if (m.Id !== id) return m;
        return { ...m, ShortId: newShortId };
      }),
    );
  };

  const handleDelete = (id: number) => {
    const y = window.scrollY; /* window: global browser window object; scrollY: current vertical scroll position */
    setMachines((prev) => prev.filter((m) => m.Id !== id));
    requestAnimationFrame(() => window.scrollTo(0, y)); /* Scroll back to the previous Y position after DOM updates */
  };

  const handleImageUpdate = (id: number, newPath: string) => {
    setMachines((prev) => prev.map((m) => (m.Id === id ? { ...m, ImagePath: newPath } : m)));
  };

  return (
    <>
      <div className={styles['header-row']}>
        <h2 className="heading">
          <MachinesIcon className="icon" />
          Machines
        </h2>
      </div>

      <form className={styles['search-form']} onSubmit={(e) => e.preventDefault()}>
        <input
          ref={inputRef}
          id={'searchInput'}
          type={'search'}
          placeholder={'Search for machines...'}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          className={styles['search-input']}
          aria-label={'Search for machines'}
          tabIndex={0}
        />

        {searchQuery.trim() === '' ? (
          <button
            type={'submit'}
            className={styles['btn-search']}
            aria-label={'Start search'}
            tabIndex={0}
            title={'Search'}
          >
            <BsSearch aria-hidden="true" />
          </button>
        ) : (
          <button
            type={'button'}
            className={styles['btn-clear']}
            aria-label={'Clear search'}
            tabIndex={0}
            title={'Clear search'}
            onClick={() => {
              setSearchQuery('');
              inputRef.current?.focus();
            }}
          >
            <BsX aria-hidden="true" />
          </button>
        )}
      </form>

      <div className={styles['expand-btn-div']}>
        <Button className={styles['expand-all-button']} onClick={() => setExpandAll((prev) => !prev)}>
          {expandAll ? 'Collapse All' : 'Expand All'}
        </Button>
      </div>

      <div className={styles['machine-grid']}>
        {filteredMachines.map((m) => (
          <MachineCard
            key={m.Id}
            machine={m}
            allShortIds={allShortIds}
            onShortIdUpdate={handleShortIdUpdate}
            onDelete={handleDelete}
            onImageUpdate={handleImageUpdate}
            expandAll={expandAll}
          />
        ))}
      </div>
    </>
  );
};

export default MachinePage;
